import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/api-client/auth.service';
import { inject } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

const SESSION_REJECTION_MESSAGES = new Set([
  'Not a valid session!',
  'Not a valid request!',
]);

/**
 * True only when auto-login confirms the Redis session is gone or the stored
 * access token no longer matches the user. Transport failures and other
 * 401s (for example "Invalid User Access") are not session rejections.
 */
export function isConfirmedSessionRejection(err: unknown): boolean {
  if (!err || typeof err !== 'object') {
    return false;
  }

  const body = err as { error?: unknown; error_code?: unknown };
  const message = typeof body.error === 'string' ? body.error : '';

  return body.error_code === 401 && SESSION_REJECTION_MESSAGES.has(message);
}

function redirectToLogin(router: Router): UrlTree {
  localStorage.clear();
  return router.parseUrl('login');
}

export const adminGuard: CanActivateFn = ():
  | boolean
  | UrlTree
  | Observable<boolean | UrlTree> => {
  const authService: AuthService = inject(AuthService);
  const router: Router = inject(Router);

  const uid: string | null = localStorage.getItem('uid');
  const accessToken: string | null = localStorage.getItem('access_token');

  if (!uid || !accessToken) {
    console.error(
      'No uid or access_token found. Redirecting back to LoginPage',
    );

    return redirectToLogin(router);
  }

  return authService.autoLogin(uid, accessToken).pipe(
    map((session) => {
      if (session?.access_token) {
        localStorage.setItem('access_token', session.access_token);
      }

      return true;
    }),
    catchError((err) => {
      if (isConfirmedSessionRejection(err)) {
        console.error(err);
        return of(redirectToLogin(router));
      }

      console.warn(
        'Auto-login unavailable. Keeping the stored session.',
        err,
      );
      return of(true);
    }),
  );
};
